import { useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { DataTable, DataTableExpandedRows, DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "@headlessui/react";
import { ArrowPathIcon, CheckBadgeIcon } from "@heroicons/react/24/solid";
import { NoSymbolIcon } from "@heroicons/react/24/outline";
import { localeShortDateTime } from "../../app/datetimeUtils";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { CustomSortIcon } from "../../components/DatatableHelpers";
import FormattedDateAndTime from "../../components/FormattedDateAndTime";
import SimplePagination from "../../components/SimplePagination";
import ChangeSecretDialog from "./ChangeSecretDialog";
import PersonName, { displayName } from "./PersonName";
import RecentChangesTable from "./RecentChangesTable";
import RestoreSecretDialog from "./RestoreSecretDialog";
import {
	SecretAuditAction,
	SecretsStatusViewModel,
	SecretVersionViewModel,
	SecretViewModel,
} from "./secretsApiSlice";

type DialogState =
	| { kind: "change"; secret: SecretViewModel }
	| { kind: "restore"; secret: SecretViewModel }
	| null;

interface Props {
	data: SecretsStatusViewModel;
}

/**
 * The secrets themselves and the audit trail. Only shown to people who can manage secrets.
 */
export default function SecretsTab({ data }: Props) {
	const [dialog, setDialog] = useState<DialogState>(null);
	const [expandedRows, setExpandedRows] = useState<
		DataTableExpandedRows | DataTableValueArray | undefined
	>(undefined);

	// Clicking anywhere on a row opens or closes its history.
	function toggleHistory(secret: SecretViewModel) {
		if (secret.versions.length === 0) return;

		setExpandedRows((previous) => {
			const rows = { ...((previous as DataTableExpandedRows) ?? {}) };
			if (rows[secret.name]) {
				delete rows[secret.name];
			} else {
				rows[secret.name] = true;
			}
			return rows;
		});
	}

	const secretBodyTemplate = (secret: SecretViewModel) => (
		<div>
			<div className="font-medium">{secret.name}</div>
			<div className="text-sm text-gray-600">{secret.description}</div>
			{secret.needsRestart && (
				<div className="text-xs text-gray-500">
					A new value is used after the site restarts.
				</div>
			)}
		</div>
	);

	const lastChangedBodyTemplate = (secret: SecretViewModel) =>
		!secret.isSet ? (
			<span className="text-gray-500">Not set</span>
		) : (
			<div>
				<FormattedDateAndTime date={secret.updatedOn ?? undefined} />
				<div className="text-sm text-gray-600">
					{secret.updatedBy
						? `by ${displayName(secret.updatedBy, secret.updatedByFullName)}`
						: "outside MI Write"}
				</div>
			</div>
		);

	// The nested version history, newest first. Never includes values.
	const versionHistoryTemplate = (secret: SecretViewModel) => {
		return <SecretHistoryTable secret={secret} />;
	};

	// The buttons shouldn't also open or close the row's history.
	const actionsBodyTemplate = (secret: SecretViewModel) => (
		<div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
			<Button
				id={`change-${secret.name}-button`}
				className="btn btn-primary h-fit w-fit whitespace-nowrap"
				disabled={!data.keyVaultAvailable}
				onClick={() => setDialog({ kind: "change", secret })}>
				Change
			</Button>
			<Button
				id={`restore-${secret.name}-button`}
				className="btn btn-neutral h-fit w-fit whitespace-nowrap"
				disabled={!data.keyVaultAvailable || !secret.hasPreviousVersion}
				title={secret.hasPreviousVersion ? undefined : "There is no previous value"}
				onClick={() => setDialog({ kind: "restore", secret })}>
				Restore Previous
			</Button>
		</div>
	);

	return (
		<div className="pt-4">
			{!data.keyVaultAvailable && (
				<AlertMessage
					className="mb-4"
					type={AlertLevel.Warning}
					message="No Key Vault is configured for this site, so secrets can't be changed here."
					showIcon
				/>
			)}
			{data.keyVaultError && (
				<AlertMessage
					className="mb-4"
					type={AlertLevel.Error}
					message={data.keyVaultError}
					showIcon
				/>
			)}
			<p className="text-sm text-gray-600">
				Secret values are never shown. Every change asks for your password, is recorded
				below and is emailed to the security contacts. A new value is used straight away on
				this server, and within {data.cacheMinutes} minute
				{data.cacheMinutes === 1 ? "" : "s"} everywhere else. You can expand a secret in the
				table to view the secret's history.
			</p>
			{data.secrets && (
				<DataTable
					value={data.secrets}
					dataKey="name"
					stripedRows
					sortIcon={CustomSortIcon}
					className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
					style={{ paddingTop: "16px" }}
					expandedRows={expandedRows}
					onRowClick={(e) => toggleHistory(e.data as SecretViewModel)}
					onRowToggle={(e) => setExpandedRows(e.data)}
					rowExpansionTemplate={versionHistoryTemplate}
					rowClassName={(secret: SecretViewModel) =>
						secret.versions.length > 0 ? "group cursor-pointer" : ""
					}>
					<Column
						expander={(secret: SecretViewModel) => secret.versions.length > 0}
						headerClassName="!pl-3 !pr-0.5 w-8"
						className="!pl-3 !pr-0.5 w-8"
					/>
					<Column header="Secret" body={secretBodyTemplate} sortable sortField="name" />
					<Column
						header="Last changed"
						body={lastChangedBodyTemplate}
						sortable
						sortField="updatedOn"
					/>
					<Column body={actionsBodyTemplate} />
				</DataTable>
			)}

			<RecentChangesTable
				changes={data.recentSecretChanges ?? []}
				filterActions={[
					SecretAuditAction.SecretChanged,
					SecretAuditAction.SecretRestored,
					SecretAuditAction.SecretChangeFailed,
					SecretAuditAction.SecretRestoreFailed,
					SecretAuditAction.PasswordCheckFailed,
				]}
				downloadPath="/Secrets/SecretHistoryCsv"
				downloadFileName="secret-history.csv"
			/>

			{dialog?.kind === "change" && (
				<ChangeSecretDialog
					secret={dialog.secret}
					cacheMinutes={data.cacheMinutes}
					onClose={() => setDialog(null)}
				/>
			)}

			{dialog?.kind === "restore" && (
				<RestoreSecretDialog secret={dialog.secret} onClose={() => setDialog(null)} />
			)}
		</div>
	);
}

// Restore uses the enabled versions only, newest first, the same as the server.
export function enabledVersions(secret: SecretViewModel) {
	return secret.versions.filter((v) => v.enabled);
}

function versionNotes(
	version: SecretVersionViewModel,
	versions: SecretVersionViewModel[],
	current?: SecretVersionViewModel
): string {
	const notes: string[] = [];
	if (version === current) {
		notes.push("In use");
	}
	if (!version.enabled) {
		notes.push("Disabled");
	}
	if (versions.find((v) => v.restoredFrom === version.version)) {
		const copy = versions.find((v) => v.restoredFrom === version.version);
		notes.push(
			copy?.createdOn
				? `Restored to ${localeShortDateTime(copy.createdOn)}`
				: "Restored to a later value"
		);
	}
	if (version.restoredFrom) {
		const source = versions.find((v) => v.version === version.restoredFrom);
		notes.push(
			source?.createdOn
				? `Restored from ${localeShortDateTime(source.createdOn)}`
				: "Restored from an earlier value"
		);
	}
	return notes.join(". ");
}

interface SecretHistoryTableProps {
	secret: SecretViewModel;
}

const historyPageSize = 5;

function SecretHistoryTable({ secret }: SecretHistoryTableProps) {
	const current = enabledVersions(secret)[0];
	const [page, setPage] = useState(0);

	const pagedVersions = useMemo(
		() => secret.versions.slice(page * historyPageSize, (page + 1) * historyPageSize),
		[secret.versions, page]
	);

	const createDateBodyTemplate = (row: SecretVersionViewModel) => {
		return <FormattedDateAndTime date={row.createdOn ?? undefined} />;
	};

	const updatedByBodyTemplate = (row: SecretVersionViewModel) => {
		return <PersonName userName={row.updatedBy} fullName={row.updatedByFullName} />;
	};

	const howBodyTemplate = (row: SecretVersionViewModel) => {
		return <span>{row.changedOnSecretsPage ? "Secrets page" : "Outside MI Write"}</span>;
	};

	const notesBodyTemplate = (row: SecretVersionViewModel, versions: SecretVersionViewModel[]) => {
		return (
			<div className="flex items-center">
				{row === current ? (
					<CheckBadgeIcon
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="current-tooltip"
					/>
				) : versions.find((v) => v.restoredFrom === row.version) ? (
					<ArrowPathIcon
						className="size-5 text-yellow-500"
						aria-hidden="true"
						data-tooltip-id="restored-tooltip"
					/>
				) : !row.enabled ? (
					<NoSymbolIcon
						className="size-5 text-red-500"
						aria-hidden="true"
						data-tooltip-id="disabled-tooltip"
					/>
				) : (
					<></>
				)}
				<span className="ml-2">{versionNotes(row, secret.versions, current)}</span>
				<Tooltip id="current-tooltip" place="top" content="In Use" />
				<Tooltip id="restored-tooltip" place="top" content="Restored" />
				<Tooltip id="disabled-tooltip" place="top" content="Disabled" />
			</div>
		);
	};

	return (
		<div className="-mx-4 -my-3 px-4 pb-3 bg-gray-100">
			<DataTable
				value={pagedVersions}
				dataKey="version"
				stripedRows
				emptyMessage="No history found"
				className="tbl !w-auto pt-3 mx-2 xl:mx-8">
				<Column header="Set On" body={createDateBodyTemplate} />
				<Column header="Updated By" body={updatedByBodyTemplate} />
				<Column header="How" body={howBodyTemplate} />
				<Column
					header="Notes"
					body={(row: SecretVersionViewModel) => notesBodyTemplate(row, secret.versions)}
				/>
			</DataTable>
			<SimplePagination
				itemCount={secret.versions.length}
				page={page}
				pageSize={historyPageSize}
				totalCount={secret.versions.length}
				isFiltered={false}
				onChange={setPage}
				className="border-none mx-2 xl:mx-8 -mt-2 !bg-gray-100"
			/>
		</div>
	);
}
